import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { Slider } from "primereact/slider";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { CircleF, GoogleMap, MarkerF, useLoadScript } from "@react-google-maps/api";
import SearchAddress from "../../../components/general/SearchAddress";
import { ProviderDirectoryMapEntry } from "../../../models/providerDirectory";
import { getAddressFromCoordinates, getCurrentLocation, phoneNumberFormatter } from "../../../utils/stringUtils";
import { getErrorTitle } from "../../../constants/constants";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import {
  dashboardPageContainerStyle,
  dashboardSectionCardStyle,
  renderDashboardHeader,
} from "../../dashboard/pages/dashboardStyles";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  appWideDialogStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";

const mapContainerStyle = {
  width: "100%",
  height: "620px",
  borderRadius: "22px",
};

const defaultMapCenter = {
  lat: 47.6062,
  lng: -122.3321,
};

const getProviderSpecialties = (specialityValue?: string | null) => (
  String(specialityValue || "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)
);

const getZoomForRadius = (radiusInMiles: number) => {
  if (radiusInMiles <= 5) {
    return 12;
  }
  if (radiusInMiles <= 10) {
    return 11;
  }
  if (radiusInMiles <= 20) {
    return 10;
  }
  if (radiusInMiles <= 35) {
    return 9;
  }
  if (radiusInMiles <= 50) {
    return 8;
  }
  return 7;
};

const ProviderDirectoryMapPage = () => {
  const { backendAPIStore } = useContext(MobXProviderContext);
  const toast = useRef<Toast>(null);
  const mobile = useResponsive().isCompact;
  const { isLoaded: isMapLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  const [loading, setLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [radiusInMiles, setRadiusInMiles] = useState(5);
  const [searchResults, setSearchResults] = useState<ProviderDirectoryMapEntry[]>([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<ProviderDirectoryMapEntry | null>(null);
  const [detailDialogVisible, setDetailDialogVisible] = useState(false);

  const mapCenter = useMemo(() => {
    if (latitude !== null && longitude !== null) {
      return { lat: latitude, lng: longitude };
    }

    if (selectedProvider?.latitude && selectedProvider?.longitude) {
      return { lat: selectedProvider.latitude, lng: selectedProvider.longitude };
    }

    return defaultMapCenter;
  }, [latitude, longitude, selectedProvider]);

  const mapZoom = useMemo(() => getZoomForRadius(radiusInMiles), [radiusInMiles]);

  const specialtyOptions = useMemo(() => {
    const uniqueSpecialties = Array.from(
      new Set(
        searchResults.flatMap((provider) => getProviderSpecialties(provider.speciality)),
      ),
    ).sort((first, second) => first.localeCompare(second));

    return uniqueSpecialties.map((specialty) => ({
      label: specialty,
      value: specialty,
    }));
  }, [searchResults]);

  const filteredSearchResults = useMemo(() => {
    if (!selectedSpecialties.length) {
      return searchResults;
    }

    return searchResults.filter((provider) => {
      const providerSpecialties = getProviderSpecialties(provider.speciality);
      return selectedSpecialties.some((selectedSpecialty) => providerSpecialties.includes(selectedSpecialty));
    });
  }, [searchResults, selectedSpecialties]);

  const runSearch = async (nextLatitude: number, nextLongitude: number, nextRadiusInMiles: number) => {
    setLoading(true);
    try {
      const response = await backendAPIStore.searchProviderDirectoryMap(
        nextLatitude,
        nextLongitude,
        nextRadiusInMiles,
        250,
      );
      setSearchResults(response.items || []);
      setSelectedSpecialties([]);
      if ((response.items || []).length > 0) {
        setSelectedProvider(response.items[0]);
      } else {
        setSelectedProvider(null);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to load the provider map right now.",
        life: 3500,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadNearbyProviders = async () => {
      setLoading(true);
      try {
        const currentLocation = await getCurrentLocation();
        const currentAddress = await getAddressFromCoordinates(currentLocation.latitude, currentLocation.longitude);
        setLatitude(currentLocation.latitude);
        setLongitude(currentLocation.longitude);
        setSelectedAddress(currentAddress || "Current location");
        await runSearch(currentLocation.latitude, currentLocation.longitude, radiusInMiles);
      } catch (error: any) {
        setLoading(false);
        toast.current?.show({
          severity: "warn",
          summary: "Location unavailable",
          detail: "We could not read your current location. Search by address to explore providers nearby.",
          life: 3500,
        });
      }
    };

    void loadNearbyProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddressChange = (event: any) => {
    if (!event?.target?.value) {
      setSelectedAddress("");
      setLatitude(null);
      setLongitude(null);
      return;
    }

    setSelectedAddress(event.target.value);
    setLatitude(typeof event.target.latitude === "number" ? event.target.latitude : null);
    setLongitude(typeof event.target.longitude === "number" ? event.target.longitude : null);
  };

  const handleSearchClick = async () => {
    if (latitude === null || longitude === null) {
      toast.current?.show({
        severity: "warn",
        summary: "Address required",
        detail: "Choose an address from the search suggestions to run a radius search.",
        life: 3000,
      });
      return;
    }

    await runSearch(latitude, longitude, radiusInMiles);
  };

  const handleUseCurrentLocation = async () => {
    setLoading(true);
    try {
      const currentLocation = await getCurrentLocation();
      const currentAddress = await getAddressFromCoordinates(currentLocation.latitude, currentLocation.longitude);
      setLatitude(currentLocation.latitude);
      setLongitude(currentLocation.longitude);
      setSelectedAddress(currentAddress || "Current location");
      await runSearch(currentLocation.latitude, currentLocation.longitude, radiusInMiles);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to use your current location.",
        life: 3500,
      });
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedAddress("");
    setLatitude(null);
    setLongitude(null);
    setRadiusInMiles(5);
    setSearchResults([]);
    setSelectedSpecialties([]);
    setSelectedProvider(null);
  };

  const openProviderDetails = (provider: ProviderDirectoryMapEntry) => {
    setSelectedProvider(provider);
    setDetailDialogVisible(true);
  };

  return (
    <div style={{ padding: mobile ? "1rem" : "1.25rem" }} className="p-component">
      <Toast ref={toast} />
      <div style={dashboardPageContainerStyle}>
        {renderDashboardHeader(
          "Provider Neighborhood Map",
          "Explore providers in your area and their proximity to you with this interactive map. Search by address, adjust your search radius, and click on providers to view details.",
          <div style={{ ...dashboardSectionCardStyle, padding: "0.8rem 1rem", boxShadow: "none" }}>
            Matches: <strong className="ml-1">{filteredSearchResults.length}</strong>
          </div>,
        )}

        <div style={dashboardSectionCardStyle}>
          <div style={{ padding: "1.25rem" }}>
            <div className="grid">
              <div className="col-12">
                <div style={{ ...appNeutralPanelStyle, padding: mobile ? "1rem" : "1.1rem 1.15rem" }}>
                  <div className="grid align-items-end">
                    <div className="col-12 lg:col-6">
                      <label className="mb-2 block">Search Center</label>
                      <div className="flex gap-2" style={{ flexDirection: mobile ? "column" as const : "row" as const, alignItems: mobile ? "stretch" : "center" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <SearchAddress
                            id="provider-directory-map-address"
                            name="providerDirectoryMapAddress"
                            onChange={handleAddressChange}
                            value={selectedAddress}
                            isSearch
                            placeholder="Search city, neighborhood, or address"
                          />
                        </div>
                        <Button
                          label="Use My Location"
                          icon="pi pi-compass"
                          text
                          style={mobile ? { width: "100%", justifyContent: "center" } : { whiteSpace: "nowrap" }}
                          onClick={() => void handleUseCurrentLocation()}
                        />
                      </div>
                    </div>
                    <div className="col-12 lg:col-3">
                      <label className="mb-2 block">Specialty</label>
                      <MultiSelect
                        value={selectedSpecialties}
                        options={specialtyOptions}
                        onChange={(e) => setSelectedSpecialties(Array.isArray(e.value) ? e.value : [])}
                        placeholder="All specialties"
                        maxSelectedLabels={2}
                        selectedItemsLabel="{0} specialties selected"
                        display="chip"
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div className="col-12 lg:col-1">
                      <label className="mb-2 block">Radius</label>
                      <div className="mb-2" style={{ fontWeight: 700, color: "#0f172a" }}>{radiusInMiles} miles</div>
                      <Slider value={radiusInMiles} onChange={(e) => setRadiusInMiles(Number(e.value))} min={5} max={75} step={5} />
                    </div>
                    <div className="col-12 lg:col-2">
                      <div className="flex gap-2" style={{ width: "100%", flexDirection: mobile ? "column" as const : "column" as const, alignItems: "stretch", justifyContent: "flex-end" }}>
                        <Button label="Search" severity="warning" rounded style={{ width: "100%" }} onClick={() => void handleSearchClick()} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid mt-3">
              <div className="col-12 lg:col-7">
                <div style={{ ...appNeutralPanelStyle, padding: "1rem" }}>
                  {!isMapLoaded || loading ? (
                    <div className="flex align-items-center justify-content-center" style={{ height: "620px" }}>
                      <ProgressSpinner />
                    </div>
                  ) : (
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={mapCenter}
                      zoom={filteredSearchResults.length > 0 || (latitude !== null && longitude !== null) ? mapZoom : 8}
                      options={{
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: false,
                      }}
                    >
                      {latitude !== null && longitude !== null ? (
                        <CircleF
                          center={{ lat: latitude, lng: longitude }}
                          radius={radiusInMiles * 1609.344}
                          options={{
                            fillColor: "#185DA2",
                            fillOpacity: 0.1,
                            strokeColor: "#185DA2",
                            strokeOpacity: 0.55,
                            strokeWeight: 2,
                          }}
                        />
                      ) : null}
                      {latitude !== null && longitude !== null ? (
                        <MarkerF
                          position={{ lat: latitude, lng: longitude }}
                          title="Search center"
                          icon={{
                            path: window.google?.maps?.SymbolPath.CIRCLE,
                            scale: 8,
                            fillColor: "#0f172a",
                            fillOpacity: 1,
                            strokeColor: "#ffffff",
                            strokeWeight: 2,
                          }}
                        />
                      ) : null}
                      {filteredSearchResults.map((provider) => (
                        provider.latitude && provider.longitude ? (
                          <MarkerF
                            key={provider.providerId}
                            position={{ lat: provider.latitude, lng: provider.longitude }}
                            title={provider.facilityName || "Provider"}
                            onClick={() => openProviderDetails(provider)}
                          />
                        ) : null
                      ))}
                    </GoogleMap>
                  )}
                </div>
              </div>

              <div className="col-12 lg:col-5">
                <div style={{ ...appNeutralPanelStyle, padding: "1rem", height: "100%" }}>
                  <div className="flex justify-content-between align-items-center gap-2 mb-3">
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Providers in Range</div>
                    <Tag value={`${filteredSearchResults.length} found`} severity="info" />
                  </div>

                  <div className="flex flex-column gap-2" style={{ maxHeight: "565px", overflowY: "auto", paddingRight: "0.25rem" }}>
                    {!loading && filteredSearchResults.length === 0 ? (
                      <div style={appMutedTextStyle}>Run a search to see nearby providers on the map and in this list.</div>
                    ) : null}

                    {filteredSearchResults.map((provider) => (
                      <div
                        key={provider.providerId}
                        style={{
                          border: selectedProvider?.providerId === provider.providerId ? "2px solid #185DA2" : "1px solid #e2e8f0",
                          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
                          borderRadius: "16px",
                          padding: "0.85rem 0.95rem",
                          background: "#ffffff",
                        }}
                      >
                        <div className="flex justify-content-between align-items-start gap-3">
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.35 }}>
                              {provider.facilityName || "Provider"}
                            </div>
                            <div className="mt-1" style={{ fontSize: "0.92rem", color: "#64748b" }}>
                              {provider.locationCity || "Unknown city"}{provider.locationCounty ? `, ${provider.locationCounty}` : ""}
                            </div>
                          </div>
                          <Tag value={`${provider.distanceInMiles || 0} mi`} severity="warning" />
                        </div>
                        <div className="grid mt-2" style={{ color: "#334155", fontSize: "0.92rem" }}>
                          <div className="col-12">
                            <strong>Status:</strong> {provider.facilityStatus || "N/A"}
                          </div>
                          <div className="col-12">
                            <strong>Address:</strong> {provider.geocodedFormattedAddress || "N/A"}
                          </div>
                          <div className="col-12 md:col-6">
                            <strong>Phone:</strong> {phoneNumberFormatter(provider.telephoneNumber)}
                          </div>
                          <div className="col-12 md:col-6">
                            <strong>Beds:</strong> {provider.licensedBedCount || "N/A"}
                          </div>
                          <div className="col-12">
                            <strong>Specialty:</strong> {provider.speciality || provider.disclosureOfServices || "N/A"}
                          </div>
                        </div>
                        <div className="mt-3 flex justify-content-end">
                          <Button label="View Details" size="small" severity="warning" rounded onClick={() => openProviderDetails(provider)} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        visible={detailDialogVisible}
        onHide={() => setDetailDialogVisible(false)}
        modal
        style={appWideDialogStyle}
        header={renderSurfaceHeader(
          selectedProvider?.facilityName || "Provider Details",
          selectedProvider?.geocodedFormattedAddress || "Address unavailable",
          "pi pi-map-marker",
        )}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
      >
        {selectedProvider ? (
          <div className="flex flex-column gap-3">
            <div style={appNeutralPanelStyle}>
              <div className="grid" style={{ color: "#334155" }}>
                {/* <div className="col-12 md:col-6"><strong>Status:</strong> {selectedProvider.facilityStatus || "N/A"}</div> */}
                <div className="col-12 md:col-6"><strong>Distance:</strong> {selectedProvider.distanceInMiles || 0} miles</div>
                <div className="col-12 md:col-6"><strong>Contact:</strong> {selectedProvider.facilityPOC || "N/A"}</div>
                <div className="col-12 md:col-6"><strong>Phone:</strong> {phoneNumberFormatter(selectedProvider.telephoneNumber)}</div>
                <div className="col-12 md:col-6"><strong>County:</strong> {selectedProvider.locationCounty || "N/A"}</div>
                <div className="col-12 md:col-6"><strong>Facility Type:</strong> {selectedProvider.facilityType || "N/A"}</div>
                <div className="col-12 md:col-6"><strong>Specialty:</strong> {selectedProvider.speciality || "N/A"}</div>
                <div className="col-12 md:col-6"><strong>Contract:</strong> {selectedProvider.contract || "N/A"}</div>
                <div className="col-12 md:col-6"><strong>Licensed Beds:</strong> {selectedProvider.licensedBedCount || "N/A"}</div>
                {/* <div className="col-12 md:col-6"><strong>Memory Care Flag:</strong> {selectedProvider.memoryCertFlag || "N/A"}</div> */}
                {/* <div className="col-12"><strong>Services Disclosure:</strong> {selectedProvider.disclosureOfServices || "N/A"}</div> */}
              </div>
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
};

export default ProviderDirectoryMapPage;
